import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { EmeraldInputModule } from '@emerald-angular/input';
import { EmeraldButtonModule } from '@emerald-angular/button';
import { EmeraldDropdownModule } from '@emerald-angular/dropdown';
import { EmeraldDatepickerModule } from '@emerald-angular/datepicker';
import { CHANGE_TEAM_CONFIG } from './change-team.config';
import { DataService } from '@app/shared/services/data.service';
import { TeamManagementService } from '../team-management.service';
import { formatDate } from '@app/shared/utils/utils';
import { dropdownValues } from '@interfaces/common.model';
import { ToasterService } from '@services/toaster.service';
@Component({
  selector: 'app-change-team',
  templateUrl: './change-team.component.html',
  styleUrl: './change-team.component.scss',
})
export class ChangeTeamComponent implements OnInit, OnChanges {
  @Output() submitted = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();
  @Input() empId!: string;
  @Input() currentProjects: any[] = [];

  changeTeamLabels = CHANGE_TEAM_CONFIG;
  newProductOptions: dropdownValues[] = [];
  currentProductOptions: dropdownValues[] = [];
  changeTeamForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private dataService: DataService,
    private teamManagementService: TeamManagementService,
    private toaster: ToasterService,
  ) {
    this.changeTeamForm = this.fb.group({
      startDate: [''],
      newProjectId: [''],
      endDate: [''],
      oldProjectId: [''],
      comments: [''],
    });
  }

  ngOnInit(): void {
    this.loadCurrentProjectOptions();
    this.loadNewProjectOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentProjects'] && !changes['currentProjects'].firstChange) {
      this.loadCurrentProjectOptions();
    }
  }

  get isSubmitDisabled(): boolean {
    const { startDate, newProjectId } = this.changeTeamForm.value;
    return !(startDate && newProjectId);
  }

  private loadNewProjectOptions(): void {
    const projects = this.dataService.projects();
    const projectList = projects?.data?.project;
    const currentProjectIds = this.currentProductOptions?.map((p) => p.value);
    this.newProductOptions =
      Array.isArray(projectList) && projectList.length > 0
        ? projectList
            ?.filter((project) => !currentProjectIds?.includes(project?.id))
            ?.map((project: any) => ({
              label: project?.name,
              value: project?.id,
            }))
        : [];
  }

  private loadCurrentProjectOptions(): void {
    const currentProjects = Array.isArray(this.currentProjects)
      ? this.currentProjects?.filter((item) => item && item?.endDate === null)
      : [];

    if (currentProjects.length > 0) {
      this.currentProductOptions = currentProjects?.map((item) => ({
        label: item?.projectName,
        value: item?.projectId,
      }));
      if (this.currentProductOptions?.length === 1) {
        const ctrl = this.changeTeamForm?.get('oldProjectId');
        if (ctrl) {
          ctrl.patchValue(this.currentProductOptions[0]);
          ctrl.markAsTouched();
          ctrl.markAsDirty();
        }
      }
    }
  }

  onClose(): void {
    this.close.emit();
  }

  onSubmit(): void {
    const formValue = { ...this.changeTeamForm.value };
    formValue.employeeId = this.empId;
    ['startDate', 'endDate'].forEach((key) => {
      if (formValue[key]) {
        const dateObj = new Date(formValue[key]);
        formValue[key] = !isNaN(dateObj.getTime())
          ? formatDate(dateObj.toISOString(), 'YYYY-MM-DD')
          : '';
      }
    });

    ['newProjectId', 'oldProjectId'].forEach((key) => {
      if (
        formValue[key] &&
        typeof formValue[key] === 'object' &&
        'value' in formValue[key]
      ) {
        formValue[key] = formValue[key].value;
      }
    });

    if (
      !('oldProjectId' in formValue) ||
      formValue.oldProjectId === '' ||
      formValue.oldProjectId === undefined
    ) {
      formValue.oldProjectId = null;
    }

    this.teamManagementService.submitChangeTeam(formValue).subscribe({
      next: (response) => {
        if (response?.status === CHANGE_TEAM_CONFIG.success) {
          this.toaster.showSuccess(
            response?.message,
            CHANGE_TEAM_CONFIG.success,
          );
          this.changeTeamForm.reset();
          this.submitted.emit();
        }
      },
      error: (error) => {
        this.toaster.showError(error?.message, CHANGE_TEAM_CONFIG.error);
      },
    });
  }
}
